import React from 'react';
/**
 * We are still seeing unexpected token errors in jest tests which could be
 * due to @babel problems that haven't been resolved or implemented
 */
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';

import { N100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { ProfileCardWrapper } from '@confluence/profile';
import { fg } from '@confluence/feature-gating';

import { ColumnWidths, TaskColumn } from './tasksDefinitions';
import { PageColumn } from './PageColumn';
import { CellContainer, CellContainerCompiled } from './presentationalComponents';
import { HeightAdjustingText } from './HeightAdjustingText';
import { TruncatedTextCacheKeys } from './TaskRowsContext';

interface MainColumnProps {
	taskId: string;
	task: any;
	mainColumn: TaskColumn;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTitleContainer = styled.span({
	color: token('color.text', N100),
	paddingLeft: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: ColumnWidths.Main,
});

const pageTitleContainerStyles = css({
	color: token('color.text', N100),
	paddingLeft: token('space.050', '4px'),
});

export const MainColumn = ({ task, taskId, mainColumn }: MainColumnProps) => {
	if (!task || !taskId) {
		return null;
	}

	if (fg('confluence_frontend_home_compiled_migration')) {
		switch (mainColumn) {
			case TaskColumn.Page:
				return (
					<PageColumn
						taskId={taskId}
						page={task.container?.title ?? ''}
						url={`${task.container?.links?.base}${task.container?.links?.webUi}`}
					/>
				);
			case TaskColumn.Space:
				return (
					<CellContainerCompiled>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */}
						<span css={pageTitleContainerStyles} style={{ width: ColumnWidths.Main }}>
							<HeightAdjustingText
								taskId={taskId}
								defaultText={task.container?.space?.name ?? ''}
								cacheKey={TruncatedTextCacheKeys.SPACE}
								url={`${task.container?.space?.links?.base}${task.container?.space?.links?.webUi}`}
							/>
						</span>
					</CellContainerCompiled>
				);
			case TaskColumn.Assignee:
				return (
					<CellContainerCompiled>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */}
						<span css={pageTitleContainerStyles} style={{ width: ColumnWidths.Main }}>
							<ProfileCardWrapper
								userId={task.assignedTo?.accountId}
								isAnonymous={!task.assignedTo?.accountId}
								canViewProfile
							>
								<HeightAdjustingText
									taskId={taskId}
									defaultText={task.assignedTo?.displayName ?? ''}
									cacheKey={TruncatedTextCacheKeys.ASSIGNEE}
								/>
							</ProfileCardWrapper>
						</span>
					</CellContainerCompiled>
				);
			case TaskColumn.Creator:
				return (
					<CellContainerCompiled>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */}
						<span css={pageTitleContainerStyles} style={{ width: ColumnWidths.Main }}>
							<ProfileCardWrapper
								userId={task.createdBy?.accountId}
								isAnonymous={!task.createdBy?.accountId}
								canViewProfile
							>
								<HeightAdjustingText
									taskId={taskId}
									defaultText={task.createdBy?.displayName ?? ''}
									cacheKey={TruncatedTextCacheKeys.CREATED_BY}
								/>
							</ProfileCardWrapper>
						</span>
					</CellContainerCompiled>
				);
		}
	}

	switch (mainColumn) {
		case TaskColumn.Page:
			return (
				<PageColumn
					taskId={taskId}
					page={task.container?.title ?? ''}
					url={`${task.container?.links?.base}${task.container?.links?.webUi}`}
				/>
			);
		case TaskColumn.Space:
			return (
				<CellContainer>
					<PageTitleContainer>
						<HeightAdjustingText
							taskId={taskId}
							defaultText={task.container?.space?.name ?? ''}
							cacheKey={TruncatedTextCacheKeys.SPACE}
							url={`${task.container?.space?.links?.base}${task.container?.space?.links?.webUi}`}
						/>
					</PageTitleContainer>
				</CellContainer>
			);
		case TaskColumn.Assignee:
			return (
				<CellContainer>
					<PageTitleContainer>
						<ProfileCardWrapper
							userId={task.assignedTo?.accountId}
							isAnonymous={!task.assignedTo?.accountId}
							canViewProfile
						>
							<HeightAdjustingText
								taskId={taskId}
								defaultText={task.assignedTo?.displayName ?? ''}
								cacheKey={TruncatedTextCacheKeys.ASSIGNEE}
							/>
						</ProfileCardWrapper>
					</PageTitleContainer>
				</CellContainer>
			);
		case TaskColumn.Creator:
			return (
				<CellContainer>
					<PageTitleContainer>
						<ProfileCardWrapper
							userId={task.createdBy?.accountId}
							isAnonymous={!task.createdBy?.accountId}
							canViewProfile
						>
							<HeightAdjustingText
								taskId={taskId}
								defaultText={task.createdBy?.displayName ?? ''}
								cacheKey={TruncatedTextCacheKeys.CREATED_BY}
							/>
						</ProfileCardWrapper>
					</PageTitleContainer>
				</CellContainer>
			);
	}
};
